import { useState, useCallback, useEffect, useRef } from "react";
import { getScoringInstructions } from "../api/scoring-api";
import type { ScoringInstruction, ScoringInstructionRow } from "../types/scoring.type";

interface UseScoringInstructionsOptions {
	rfxId: number | undefined;
}

interface UseScoringInstructionsResult {
	error: string | null;
	instructions: ScoringInstruction | null;
	isLoading: boolean;
	getActiveRow: (score: number | null) => ScoringInstructionRow | null;
}

export const useScoringInstructions = ({
	rfxId,
}: UseScoringInstructionsOptions): UseScoringInstructionsResult => {
	const [instructions, setInstructions] = useState<ScoringInstruction | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const hasFetched = useRef(false);

	const fetchInstructions = useCallback(async () => {
		if (!rfxId || hasFetched.current) {
			return;
		}
		setIsLoading(true);
		setError(null);
		try {
			const data = await getScoringInstructions(rfxId);
			setInstructions(data);
			hasFetched.current = true;
		} catch {
			setError("Failed to load scoring instructions");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId]);

	useEffect(() => {
		fetchInstructions();
	}, [fetchInstructions]);

	const getActiveRow = useCallback(
		(score: number | null): ScoringInstructionRow | null => {
			if (score === null || !instructions) {
				return null;
			}
			return (
				instructions.rows.find(
					(row) => score >= row.minScore && score <= row.maxScore
				) ?? null
			);
		},
		[instructions]
	);

	return {
		instructions,
		isLoading,
		error,
		getActiveRow,
	};
};
